import { AuthenticationError } from '../model/AuthenticationError';

function dedupe<T>(arr: T[]): T[] {
  return arr.filter((x, i) => arr.indexOf(x) === i);
}

export function getUniqueScopes(...scopes: string[]) {
  const scopeString = scopes.filter(Boolean).join();
  return dedupe(scopeString.replace(/\s/g, ',').split(',')).join(' ').trim();
}

export function createRandomString() {
  const charset =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_~.';
  const randomValues: number[] = Array.from(
    window.crypto.getRandomValues(new Uint8Array(48))
  );
  let random = '';
  randomValues.forEach((v) => (random += charset[v % charset.length]));
  return random;
}

export function sha256(s: string) {
  return window.crypto.subtle.digest(
    { name: 'SHA-256' },
    new TextEncoder().encode(s)
  );
}

function urlEncodeB64(input: string) {
  const b64Chars: Record<string, string> = { '+': '-', '/': '_', '=': '' };
  return input.replace(/[+/=]/g, (m) => b64Chars[m]);
}

function decodeB64(input: string) {
  return decodeURIComponent(
    atob(input)
      .split('')
      .map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
}

export function urlDecodeB64(input: string) {
  return decodeB64(input.replace(/_/g, '/').replace(/-/g, '+'));
}

export function bufferToBase64UrlEncoded(input: ArrayBuffer) {
  const ie11SafeInput = new Uint8Array(input);
  return urlEncodeB64(
    window.btoa(String.fromCharCode(...Array.from(ie11SafeInput)))
  );
}

async function getJSON(url: string, options?: RequestInit) {
  const response = await fetch(url, options);
  const { error, error_description, ...success } = await response.json();
  if (!response.ok) {
    throw new AuthenticationError(
      error || 'request_error',
      error_description || `HTTP error. Unable to fetch ${url}`
    );
  }
  return success;
}

export async function oauthToken(
  tokenEndpoint: string,
  options: Record<string, string>
) {
  return await getJSON(tokenEndpoint, {
    method: 'POST',
    body: new URLSearchParams(options),
    headers: {
      'Content-type': 'application/x-www-form-urlencoded'
    }
  });
}

export function validateCrypto() {
  if (!window.crypto) {
    throw new Error(
      'For security reasons, `window.crypto` is required to run this library.'
    );
  }
  if (!window.crypto.subtle) {
    throw new Error(`
      This library must run on a secure origin.
      See https://github.com/auth0/auth0-spa-js/blob/master/FAQ.md#why-do-i-get-auth0-spa-js-must-run-on-a-secure-origin 
      for more information.
    `);
  }
}

export function getUrlParam(name: string) {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}
